<template>
    <div class="content-wrapper business-pulse-v2">
        <Breadcrumbs :items="breadcrumbs" />

        <section v-if="project && !hasBusinessPulseAccess" class="report20 r20-gate">
            <div class="shell">
                <header class="page-head">
                    <p class="r20-gate-eyebrow">Модуль Data Heroes</p>
                    <h1>Отчёт 2.0</h1>
                    <p>
                        Пульс вашего бизнеса: 17 показателей, графики и сравнение с другими периодами.
                    </p>
                </header>
                <div class="r20-gate-card">
                    <h2>Финансы, клиенты и записи на одном экране</h2>
                    <ul class="r20-gate-facts">
                        <li>17 фактических показателей бизнеса</li>
                        <li>Финансы, клиенты и записи в одном экране</li>
                        <li>Сравнение с предыдущим периодом или прошлым годом</li>
                        <li>График выручки, записей и среднего чека</li>
                    </ul>
                    <div class="r20-gate-actions">
                        <button
                            v-if="showTrialButton"
                            type="button"
                            class="btn r20-primary"
                            @click="trialOpen = true"
                        >
                            {{ trialButtonText }}
                        </button>
                        <button type="button" class="btn r20-gate-buy" @click="buyModule">Купить модуль</button>
                    </div>
                    <p class="r20-gate-note">Пробный период начинается только после активации.</p>
                </div>
            </div>
        </section>

        <main v-else-if="hasBusinessPulseAccess" class="report20">
            <div class="shell">
                <header class="page-head">
                    <h1>Отчёт 2.0</h1>
                    <p>
                        Ключевые финансовые, клиентские и операционные показатели за выбранный период с
                        возможностью сравнить с предыдущим периодом.
                    </p>
                </header>

                <section class="filter-panel" aria-label="Фильтры отчёта">
                    <div class="filter-grid">
                        <div class="field employee-field">
                            <span id="r20-employee-label" class="field-label">Сотрудники</span>
                            <details
                                ref="employeePicker"
                                class="employee-picker"
                                :open="employeeOpen"
                                @toggle="employeeOpen = $event.target.open"
                            >
                                <summary
                                    class="control"
                                    aria-labelledby="r20-employee-label r20-employee-summary"
                                    :title="employeeSummary"
                                >
                                    <span id="r20-employee-summary">{{ employeeSummary }}</span>
                                </summary>
                                <div class="employee-list">
                                    <label class="check-row">
                                        <input
                                            ref="employeeAll"
                                            type="checkbox"
                                            :checked="allStaffSelected"
                                            :indeterminate.prop="someStaffSelected"
                                            @change="toggleAllStaff($event.target.checked)"
                                        />Все сотрудники
                                    </label>
                                    <label v-for="item in staffOptions" :key="item.id" class="check-row">
                                        <input
                                            type="checkbox"
                                            :checked="isStaffSelected(item.id)"
                                            @change="toggleStaff(item.id, $event.target.checked)"
                                        />{{ item.name }}
                                    </label>
                                    <p v-if="!staffOptions.length" class="check-row">
                                        Сотрудники появятся после загрузки отчёта
                                    </p>
                                </div>
                            </details>
                        </div>

                        <div class="field comparison-field">
                            <label for="r20-comparison">Сравнение</label>
                            <select
                                id="r20-comparison"
                                class="control"
                                :value="comparison"
                                @change="setComparison($event.target.value)"
                            >
                                <option value="previous">Предыдущий период</option>
                                <option value="year">Прошлый год</option>
                                <option value="none">Без сравнения</option>
                            </select>
                        </div>

                        <div class="field range-field">
                            <label for="r20-calendar-trigger">Выберите период</label>
                            <div ref="datePicker" class="date-picker" @keydown.esc="closePicker(true)">
                                <button
                                    id="r20-calendar-trigger"
                                    ref="calendarTrigger"
                                    type="button"
                                    class="control calendar-trigger"
                                    aria-haspopup="dialog"
                                    :aria-expanded="String(picker.open)"
                                    :title="currentRange ? fromTo(currentRange) : 'Вся история'"
                                    @click="picker.open ? closePicker(true) : openPicker()"
                                >
                                    <span>{{ currentRange ? interval(currentRange) : 'Всё время' }}</span>
                                    <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                        <rect
                                            x="3"
                                            y="4.5"
                                            width="14"
                                            height="12.5"
                                            rx="2"
                                            stroke="currentColor"
                                            stroke-width="1.5"
                                        />
                                        <path
                                            d="M6.5 3v3M13.5 3v3M3 8.5h14"
                                            stroke="currentColor"
                                            stroke-width="1.5"
                                            stroke-linecap="round"
                                        />
                                    </svg>
                                </button>
                                <div
                                    v-if="picker.open"
                                    class="calendar-popover"
                                    role="dialog"
                                    aria-label="Выбор периода"
                                >
                                    <div class="calendar-head">
                                        <button
                                            type="button"
                                            class="calendar-nav"
                                            :aria-label="
                                                picker.mode === 'months'
                                                    ? 'Предыдущий год'
                                                    : 'Предыдущий месяц'
                                            "
                                            @click="navigatePicker(-1)"
                                        >
                                            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                                <path
                                                    d="m12 5 -5 5 5 5"
                                                    stroke="currentColor"
                                                    stroke-width="1.6"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            class="calendar-title-button"
                                            :aria-expanded="String(picker.mode === 'months')"
                                            title="Выбрать месяц и год"
                                            @click="picker.mode = picker.mode === 'days' ? 'months' : 'days'"
                                        >
                                            <span>{{ pickerMonthLabel }}</span>
                                            <svg viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                                <path
                                                    d="m4 6 4 4 4-4"
                                                    stroke="currentColor"
                                                    stroke-width="1.5"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                        </button>
                                        <button
                                            type="button"
                                            class="calendar-nav"
                                            :aria-label="
                                                picker.mode === 'months' ? 'Следующий год' : 'Следующий месяц'
                                            "
                                            @click="navigatePicker(1)"
                                        >
                                            <svg viewBox="0 0 20 20" fill="none" aria-hidden="true">
                                                <path
                                                    d="m8 5 5 5 -5 5"
                                                    stroke="currentColor"
                                                    stroke-width="1.6"
                                                    stroke-linecap="round"
                                                    stroke-linejoin="round"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                    <div class="calendar-inputs">
                                        <div class="field">
                                            <label for="r20-date-start">Первая дата</label>
                                            <input
                                                id="r20-date-start"
                                                class="control"
                                                type="text"
                                                inputmode="numeric"
                                                autocomplete="off"
                                                placeholder="ДД.ММ.ГГГГ"
                                                maxlength="10"
                                                :value="pickerInputDate(picker.start)"
                                                @change="onPickerInput('start', $event.target.value)"
                                            />
                                        </div>
                                        <div class="field">
                                            <label for="r20-date-end">Последняя дата</label>
                                            <input
                                                id="r20-date-end"
                                                class="control"
                                                type="text"
                                                inputmode="numeric"
                                                autocomplete="off"
                                                placeholder="ДД.ММ.ГГГГ"
                                                maxlength="10"
                                                :value="pickerInputDate(picker.end)"
                                                @change="onPickerInput('end', $event.target.value)"
                                            />
                                        </div>
                                    </div>
                                    <p class="calendar-help">
                                        {{
                                            picker.mode === 'months'
                                                ? 'Выберите год, затем месяц.'
                                                : picker.pickingEnd
                                                ? 'Теперь выберите последнюю дату.'
                                                : 'Нажмите первую дату, затем последнюю.'
                                        }}
                                    </p>
                                    <div v-if="picker.mode === 'days'" class="calendar-months">
                                        <div class="calendar-month">
                                            <div class="calendar-weekdays" aria-hidden="true">
                                                <span v-for="weekday in weekdays" :key="weekday">{{
                                                    weekday
                                                }}</span>
                                            </div>
                                            <div
                                                class="calendar-grid"
                                                role="group"
                                                :aria-label="pickerMonthLabel"
                                            >
                                                <template v-for="cell in pickerCells">
                                                    <span
                                                        v-if="!cell.date"
                                                        :key="cell.key"
                                                        class="calendar-blank"
                                                        aria-hidden="true"
                                                    ></span>
                                                    <button
                                                        v-else
                                                        :key="cell.key"
                                                        type="button"
                                                        class="calendar-cell"
                                                        :class="{
                                                            'is-selected': cell.selected,
                                                            'range-start': cell.date === picker.start,
                                                            'range-end': cell.date === picker.end,
                                                            'is-current': cell.date === today,
                                                        }"
                                                        :aria-label="fmtDate(parseDate(cell.date))"
                                                        :aria-pressed="
                                                            String(
                                                                cell.selected || cell.date === picker.start
                                                            )
                                                        "
                                                        @click="pickDate(cell.date)"
                                                    >
                                                        {{ cell.day }}
                                                    </button>
                                                </template>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="calendar-month-chooser">
                                        <div class="calendar-year-field">
                                            <label for="r20-calendar-year">Год</label>
                                            <select
                                                id="r20-calendar-year"
                                                class="control"
                                                :value="pickerYear"
                                                @change="setPickerYear($event.target.value)"
                                            >
                                                <option
                                                    v-for="year in pickerYears"
                                                    :key="year"
                                                    :value="year"
                                                    >{{ year }}</option
                                                >
                                            </select>
                                        </div>
                                        <div
                                            class="calendar-month-options"
                                            role="group"
                                            aria-label="Выберите месяц"
                                        >
                                            <button
                                                v-for="(name, month) in monthNames"
                                                :key="name"
                                                type="button"
                                                class="calendar-month-option"
                                                :aria-pressed="String(month === pickerMonth)"
                                                @click="setPickerMonth(month)"
                                            >
                                                {{ name }}
                                            </button>
                                        </div>
                                    </div>
                                    <p class="r20-error" role="alert">{{ picker.error }}</p>
                                    <div v-if="picker.mode === 'days'" class="calendar-bottom">
                                        <strong aria-live="polite">{{ pickerPreview }}</strong>
                                        <button
                                            type="button"
                                            class="btn r20-primary"
                                            :disabled="Boolean(pickerValidation)"
                                            @click="applyPicker"
                                        >
                                            Показать
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="presets" role="group" aria-label="Быстрый выбор дат">
                            <button
                                v-for="item in presets"
                                :key="item.value"
                                class="preset"
                                type="button"
                                :data-preset="item.value"
                                :aria-pressed="String(preset === item.value)"
                                :title="item.title"
                                @click="setPreset(item.value)"
                            >
                                {{ item.label }}
                            </button>
                        </div>
                    </div>

                    <div class="parameters compact-parameters">
                        <h2 class="sr-only">Параметры отчёта</h2>
                        <div class="parameter">
                            <span>Показываем:</span>
                            <strong>{{ currentRange ? interval(currentRange) : 'Вся история' }}</strong>
                        </div>
                        <div class="parameter">
                            <span>Сравниваем:</span>
                            <strong>{{
                                comparisonRange
                                    ? interval(comparisonRange)
                                    : comparison === 'none' || preset === 'all'
                                    ? 'Без сравнения'
                                    : '—'
                            }}</strong>
                        </div>
                        <span class="comparison-caption">{{ comparisonHelp }}</span>
                    </div>
                    <p v-if="noStaffSelected" class="empty-notice visible">
                        Выберите хотя бы одного сотрудника, чтобы увидеть показатели.
                    </p>
                </section>

                <div
                    v-if="additionalUnconfigured"
                    ref="allocationBar"
                    class="allocation-bar"
                    :class="{ 'focus-flash': allocationFlash }"
                >
                    <div class="allocation-message">
                        <span class="warning-icon" aria-hidden="true">!</span>
                        <div>
                            <strong>Дополнительные услуги не распределены</strong>
                            <p>Распределите услуги, чтобы видеть статистику по ним.</p>
                        </div>
                    </div>
                    <button
                        v-if="canConfigureServices"
                        ref="distributeButton"
                        type="button"
                        class="btn"
                        @click="openServicesDialog"
                    >
                        Распределить услуги <span aria-hidden="true">↗</span>
                    </button>
                </div>

                <p v-if="error" class="r20-error r20-load-error" role="alert">
                    Не удалось загрузить отчёт.
                    <button type="button" class="allocation-edit" @click="loadReport">Повторить</button>
                </p>

                <div
                    v-if="x && !noStaffSelected"
                    class="r20-body"
                    :class="{ 'r20-body--loading': loading }"
                    :aria-busy="String(loading)"
                >
                    <section class="section finance" aria-labelledby="r20-finance-heading">
                        <div class="section-heading">
                            <h2 id="r20-finance-heading">Финансы</h2>
                            <div class="section-heading-right">
                                <button
                                    v-if="!additionalUnconfigured && canConfigureServices"
                                    type="button"
                                    class="allocation-edit"
                                    @click="openServicesDialog"
                                >
                                    Распределение услуг
                                </button>
                                <span class="date-tag">{{ periodTag }}</span>
                            </div>
                        </div>

                        <div class="finance-top">
                            <article class="metric featured">
                                <h3>Выручка</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('revenue', x.revenue)
                                    }}</strong>
                                    <MetricDelta :delta="delta('revenue')" />
                                </div>
                                <div class="how">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Основные услуги + дополнительные услуги + товары и абонементы
                                        <span class="report-dates">{{ t }}</span
                                        >.
                                    </p>
                                </div>
                            </article>
                            <article class="metric">
                                <h3>Средняя выручка за день</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{ formatValue('daily', x.daily) }}</strong>
                                    <MetricDelta :delta="delta('daily')" />
                                </div>
                                <div class="how mint">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Выручка ÷ кол-во календарных дней этого периода
                                        <span class="report-dates">{{ t }}</span
                                        >.
                                    </p>
                                </div>
                            </article>
                            <article class="metric">
                                <h3>Средний чек</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('average', x.average)
                                    }}</strong>
                                    <MetricDelta :delta="delta('average')" />
                                </div>
                                <div class="how sand">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Выручка ÷ количество завершённых визитов
                                        <span class="report-dates">{{ t }}</span
                                        >. Подробнее про записи и клиентов вы увидите после графика внизу
                                        страницы.
                                    </p>
                                    <a
                                        class="small-link"
                                        href="#r20-bookings-heading"
                                        @click.prevent="scrollTo('r20-bookings-heading')"
                                        >Подробнее ↓</a
                                    >
                                </div>
                            </article>
                        </div>

                        <div class="finance-bottom">
                            <article class="metric">
                                <h3>Выручка по услугам</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('services', x.services)
                                    }}</strong>
                                    <MetricDelta :delta="delta('services')" />
                                </div>
                                <div class="how">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Сумма дохода от основных услуг
                                        <span class="report-dates">{{ t }}</span
                                        >. Если дополнительные услуги не распределены, сюда входит выручка по
                                        всем услугам.
                                    </p>
                                </div>
                            </article>
                            <article class="metric">
                                <h3>Выручка по товарам и абонементам</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('products', x.products)
                                    }}</strong>
                                    <MetricDelta :delta="delta('products')" />
                                </div>
                                <div class="how mint">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Продажи товаров и абонементов <span class="report-dates">{{ t }}</span
                                        >. Услуги в эту сумму не входят.
                                    </p>
                                </div>
                            </article>
                            <article class="metric">
                                <h3>Выручка по дополнительным услугам</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('additional', x.additional)
                                    }}</strong>
                                    <MetricDelta :delta="delta('additional')" />
                                </div>
                                <div class="how sand">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Выручка <span class="report-dates">{{ t }}</span> по услугам, которые
                                        вы распределили как дополнительные.<template
                                            v-if="canConfigureServices"
                                        >
                                            <a href="#" @click.prevent="onAllocationLink">Распределить</a
                                            >.</template
                                        >
                                    </p>
                                </div>
                            </article>
                            <article class="metric">
                                <h3>Средний чек по дополнительным услугам</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('additionalAverage', x.additionalAverage)
                                    }}</strong>
                                    <MetricDelta :delta="delta('additionalAverage')" />
                                </div>
                                <div class="how">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Выручка по доп. услугам ÷ число завершённых визитов хотя бы с одной
                                        доп. услугой
                                        <span class="report-dates">{{ t }}</span
                                        >.
                                    </p>
                                    <p v-if="additionalUnconfigured" class="additional-setup">
                                        <a
                                            v-if="canConfigureServices"
                                            href="#"
                                            @click.prevent="onAllocationLink"
                                            >Распределите</a
                                        ><template v-else>Распределите</template>
                                        доп. услуги, чтобы получать статистику по ним.
                                    </p>
                                </div>
                            </article>
                        </div>

                        <div class="chart-card">
                            <div class="chart-controls">
                                <div class="chart-step">
                                    <label for="r20-chart-step">Шаг графика</label>
                                    <output
                                        id="r20-chart-step"
                                        class="control"
                                        aria-live="polite"
                                        title="Шаг графика определяется выбранными датами автоматически"
                                    >
                                        {{ chartStepLabel }}
                                    </output>
                                </div>
                                <div
                                    class="chart-tabs"
                                    role="tablist"
                                    aria-label="Показатель графика"
                                    @keydown="onChartTabsKeydown"
                                >
                                    <button
                                        v-for="tab in chartTabs"
                                        :key="tab.value"
                                        :ref="`chart-tab-${tab.value}`"
                                        type="button"
                                        class="chart-tab"
                                        role="tab"
                                        :aria-selected="String(chart === tab.value)"
                                        @click="chart = tab.value"
                                    >
                                        {{ tab.label }}
                                    </button>
                                </div>
                            </div>
                            <div class="chart-overview">
                                <div>
                                    <div class="chart-summary">
                                        <div class="chart-total-line">
                                            <strong>{{ formatChartValue(chartTotal) }}</strong>
                                            <MetricDelta
                                                :delta="delta(chartKey, chart === 'bookings' ? 'шт.' : '')"
                                                aria-label="Изменение к датам сравнения"
                                            />
                                        </div>
                                        <span>{{ chartCaption }}</span>
                                    </div>
                                    <div class="legend">
                                        <span>
                                            <i></i>
                                            <span
                                                >Показываем:
                                                {{ currentRange ? interval(currentRange) : '' }}</span
                                            >
                                            <b>{{ formatChartValue(chartTotal) }}</b>
                                        </span>
                                        <span v-if="b && comparisonRange">
                                            <i></i>
                                            <span>Сравниваем: {{ interval(comparisonRange) }}</span>
                                            <b>{{ formatChartValue(b[chartKey]) }}</b>
                                        </span>
                                    </div>
                                </div>
                                <div class="chart-extrema">
                                    <div>
                                        <span>Максимум</span>
                                        <strong>{{
                                            chartExtrema.max ? formatChartValue(chartExtrema.max.value) : '—'
                                        }}</strong>
                                        <small>{{
                                            chartExtrema.max
                                                ? chartExtremaLabel(chartExtrema.max)
                                                : 'Нет данных'
                                        }}</small>
                                    </div>
                                    <div>
                                        <span>Минимум</span>
                                        <strong>{{
                                            chartExtrema.min ? formatChartValue(chartExtrema.min.value) : '—'
                                        }}</strong>
                                        <small>{{
                                            chartExtrema.min
                                                ? chartExtremaLabel(chartExtrema.min)
                                                : 'Нет данных'
                                        }}</small>
                                    </div>
                                </div>
                            </div>
                            <div ref="chartWrap" class="chart-wrap">
                                <svg
                                    :viewBox="`0 0 ${chartGeometry.width} ${chartGeometry.height}`"
                                    role="img"
                                    :aria-label="chartCaption"
                                >
                                    <template v-for="line in chartGeometry.grid">
                                        <line
                                            :key="`l${line.y}`"
                                            :x1="chartGeometry.left"
                                            :y1="line.y"
                                            :x2="chartGeometry.width - chartGeometry.right"
                                            :y2="line.y"
                                            stroke="#edf0f6"
                                        />
                                        <text
                                            :key="`t${line.y}`"
                                            :x="chartGeometry.left - 8"
                                            :y="line.y + 3"
                                            text-anchor="end"
                                        >
                                            {{ line.label }}
                                        </text>
                                    </template>
                                    <template v-for="bar in chartGeometry.bars">
                                        <rect
                                            :key="bar.key"
                                            data-chart-bar
                                            :data-series="bar.series"
                                            tabindex="0"
                                            role="button"
                                            :aria-label="bar.aria"
                                            :x="bar.x"
                                            :y="bar.y"
                                            :width="bar.width"
                                            :height="bar.height"
                                            rx="2"
                                            :fill="bar.series === 'current' ? '#525ad5' : '#A0AAE4'"
                                            @mouseenter="showTip(bar.index, $event)"
                                            @focus="showTip(bar.index, $event)"
                                            @click="showTip(bar.index, $event)"
                                            @mouseleave="tip = null"
                                            @blur="tip = null"
                                        />
                                    </template>
                                    <text
                                        v-for="label in chartGeometry.labels"
                                        :key="`x${label.index}`"
                                        :x="label.x"
                                        :y="chartGeometry.height - 5"
                                        text-anchor="middle"
                                    >
                                        {{ label.text }}
                                    </text>
                                </svg>
                                <div
                                    v-if="tip"
                                    ref="chartTip"
                                    class="chart-tip visible"
                                    role="status"
                                    :style="{ left: `${tip.left}px`, top: `${tip.top}px` }"
                                >
                                    <div v-for="line in tip.lines" :key="line">{{ line }}</div>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section class="section clients" aria-labelledby="r20-clients-heading">
                        <div class="section-heading">
                            <h2 id="r20-clients-heading">Клиенты</h2>
                            <div class="section-heading-right">
                                <span class="date-tag">{{ periodTag }}</span>
                            </div>
                        </div>
                        <div class="client-grid">
                            <article class="metric client-total">
                                <h3>Всего клиентов</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('clients', x.clients)
                                    }}</strong>
                                    <MetricDelta :delta="delta('clients')" />
                                </div>
                                <div class="total-client-caption">С завершёнными визитами</div>
                                <div class="how">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Клиенты с завершёнными визитами
                                        <span class="report-dates">{{ t }}</span
                                        >.
                                    </p>
                                </div>
                            </article>
                            <article class="metric">
                                <h3>Постоянные клиенты</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('permanentClients', x.permanentClients)
                                    }}</strong>
                                    <MetricDelta :delta="delta('permanentClients')" />
                                </div>
                                <div class="share">
                                    <p>
                                        Доля от всех клиентов<span
                                            >{{ num(x.permanentClients) }} из
                                            {{ num(x.clients) }} клиентов</span
                                        >
                                    </p>
                                    <b>{{ formatValue('permanentShare', x.permanentShare) }}</b>
                                </div>
                                <div class="how mint">
                                    <strong>Как считаем</strong>
                                    <p>
                                        У клиента было два или больше завершённых визитов до
                                        <span class="report-dates">{{ startLabel }}</span
                                        >, и он пришёл <span class="report-dates">{{ t }}</span
                                        >.
                                    </p>
                                </div>
                            </article>
                            <article class="metric">
                                <h3>Повторные клиенты</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('repeatClients', x.repeatClients)
                                    }}</strong>
                                    <MetricDelta :delta="delta('repeatClients')" />
                                </div>
                                <div class="share">
                                    <p>
                                        Доля от всех клиентов<span
                                            >{{ num(x.repeatClients) }} из {{ num(x.clients) }} клиентов</span
                                        >
                                    </p>
                                    <b>{{ formatValue('repeatShare', x.repeatShare) }}</b>
                                </div>
                                <div class="how sand">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Первый визит клиента был до
                                        <span class="report-dates">{{ startLabel }}</span
                                        >, и второй раз он пришёл <span class="report-dates">{{ t }}</span
                                        >.
                                    </p>
                                </div>
                            </article>
                            <article class="metric">
                                <h3>Новые клиенты</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('newClients', x.newClients)
                                    }}</strong>
                                    <MetricDelta :delta="delta('newClients')" />
                                </div>
                                <div class="share">
                                    <p>
                                        Доля от всех клиентов<span
                                            >{{ num(x.newClients) }} из {{ num(x.clients) }} клиентов</span
                                        >
                                    </p>
                                    <b>{{ formatValue('newShare', x.newShare) }}</b>
                                </div>
                                <div class="how">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Первый завершённый визит клиента состоялся
                                        <span class="report-dates">{{ t }}</span
                                        >.
                                    </p>
                                </div>
                            </article>
                        </div>

                        <p class="client-row-label">Возвращаемость клиентов</p>
                        <div class="retention-grid">
                            <article v-for="card in retentionCards" :key="card.key" class="metric">
                                <h3>{{ card.title }}</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue(card.key, x[card.key])
                                    }}</strong>
                                    <MetricDelta :delta="delta(card.key)" />
                                </div>
                                <div class="share">
                                    <p>
                                        {{ card.share
                                        }}<span
                                            >{{ num(x[card.key]) }} из {{ num(x[card.base]) }} клиентов</span
                                        >
                                    </p>
                                    <b>{{ formatValue(card.rate, x[card.rate]) }}</b>
                                </div>
                                <div class="how" :class="card.tone">
                                    <div class="hint-main">
                                        <strong>Как считаем</strong>
                                        <p>
                                            {{ card.before }} <span class="report-dates">{{ baselineT }}</span
                                            >{{ card.middle }} <span class="report-dates">{{ t }}</span
                                            >.{{ card.after }}
                                        </p>
                                    </div>
                                    <p class="hint-note">Рекомендуем смотреть за 3 месяца и более.</p>
                                </div>
                            </article>
                        </div>
                    </section>

                    <section class="section bookings" aria-labelledby="r20-bookings-heading">
                        <div class="section-heading">
                            <h2 id="r20-bookings-heading">Записи</h2>
                            <div class="section-heading-right">
                                <span class="date-tag">{{ periodTag }}</span>
                            </div>
                        </div>
                        <p class="booking-row-label">Записи повторных, постоянных и новых клиентов</p>
                        <div class="bookings-grid">
                            <article class="metric featured">
                                <h3>Всего записей</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('bookings', x.bookings)
                                    }}</strong>
                                    <MetricDelta :delta="delta('bookings')" />
                                </div>
                                <div class="how">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Все созданные записи <span class="report-dates">{{ t }}</span
                                        >: завершённые, отменённые и в других статусах.
                                    </p>
                                </div>
                            </article>
                            <article class="metric">
                                <h3>Записи повторных и постоянных клиентов</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('repeatBookings', x.repeatBookings)
                                    }}</strong>
                                    <MetricDelta :delta="delta('repeatBookings')" />
                                </div>
                                <div class="how mint">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Количество созданных записей
                                        <span class="report-dates">{{ t }}</span> у клиентов, у которых был
                                        хотя бы один завершённый визит до
                                        <span class="report-dates">{{ startLabel }}</span
                                        >.
                                    </p>
                                </div>
                            </article>
                            <article class="metric">
                                <h3>Записи новых клиентов</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('newBookings', x.newBookings)
                                    }}</strong>
                                    <MetricDelta :delta="delta('newBookings')" />
                                </div>
                                <div class="how sand">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Количество созданных записей
                                        <span class="report-dates">{{ t }}</span> у клиентов, у которых не
                                        было завершённых визитов до
                                        <span class="report-dates">{{ startLabel }}</span
                                        >. У нового клиента может быть несколько записей.
                                    </p>
                                </div>
                            </article>
                        </div>
                        <p class="booking-row-label status-row-label">Те же записи — по статусу</p>
                        <div class="status-grid">
                            <article class="metric status-card">
                                <h3>Завершённые записи</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('completed', x.completed)
                                    }}</strong>
                                    <MetricDelta :delta="delta('completed')" />
                                </div>
                                <div class="how mint">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Записи, созданные <span class="report-dates">{{ t }}</span
                                        >, в которых хотя бы по одной услуге установлен статус «Клиент
                                        пришёл».
                                    </p>
                                </div>
                            </article>
                            <article class="metric status-card">
                                <h3>Отменённые записи</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{
                                        formatValue('canceled', x.canceled)
                                    }}</strong>
                                    <MetricDelta :delta="delta('canceled')" />
                                </div>
                                <div class="how sand">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Записи, созданные <span class="report-dates">{{ t }}</span
                                        >, в которых отменены все услуги.
                                    </p>
                                    <div class="cancel-split">
                                        <div>
                                            <span>Повторных и постоянных</span>
                                            <strong>{{ num(x.canceledRepeat) }}</strong>
                                        </div>
                                        <div>
                                            <span>Новых клиентов</span>
                                            <strong>{{ num(x.canceledNew) }}</strong>
                                        </div>
                                    </div>
                                </div>
                            </article>
                            <article class="metric status-card">
                                <h3>В других статусах</h3>
                                <div class="metric-main">
                                    <strong class="metric-value">{{ formatValue('other', x.other) }}</strong>
                                    <MetricDelta :delta="delta('other')" />
                                </div>
                                <div class="how">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Записи, созданные <span class="report-dates">{{ t }}</span
                                        >, которые не завершены и не отменены полностью: «Ожидание»,
                                        «Подтвердил», «Не пришёл» без отмены.
                                    </p>
                                </div>
                            </article>
                        </div>
                    </section>

                    <section class="section opz" aria-labelledby="r20-opz-heading">
                        <div class="section-heading">
                            <h2 id="r20-opz-heading">ОПЗ <small>оперативная повторная запись</small></h2>
                            <div class="section-heading-right">
                                <span class="date-tag">{{ periodTag }}</span>
                            </div>
                        </div>
                        <div class="opz-block">
                            <div>
                                <div class="opz-main">
                                    <strong class="opz-value">{{ formatValue('opzRate', x.opzRate) }}</strong>
                                    <MetricDelta :delta="delta('opzRate')" />
                                </div>
                                <p class="opz-count">
                                    {{ num(x.opz) }} из {{ num(x.opzVisits) }} завершённых визитов с ОПЗ
                                </p>
                                <div class="how mint">
                                    <strong>Как считаем</strong>
                                    <p>
                                        Завершённые визиты, после которых оформлена оперативная повторная
                                        запись ÷ все завершённые визиты × 100%
                                        <span class="report-dates">{{ t }}</span
                                        >.
                                    </p>
                                </div>
                            </div>
                            <div class="opz-promo">
                                <svg
                                    class="promo-pattern"
                                    viewBox="0 0 500 360"
                                    fill="none"
                                    aria-hidden="true"
                                >
                                    <defs>
                                        <pattern
                                            id="opz-brand-pattern"
                                            width="100"
                                            height="90"
                                            patternUnits="userSpaceOnUse"
                                            patternTransform="rotate(-12)"
                                        >
                                            <path
                                                d="M60 4 31 45h26L45 80l38-49H59L71 4Z"
                                                stroke="#C4CCFF"
                                                stroke-width="1.1"
                                            />
                                            <path d="M7 22h10m-5-5v10" stroke="#C4CCFF" stroke-width="1.2" />
                                        </pattern>
                                    </defs>
                                    <rect width="500" height="360" fill="url(#opz-brand-pattern)" />
                                    <path
                                        d="M366 43 258 194h86l-35 99 129-165h-82l34-85Z"
                                        fill="#D4EC38"
                                        fill-opacity=".7"
                                    />
                                </svg>
                                <span class="promo-eyebrow">Модуль Data Heroes</span>
                                <h3>Больше повторных записей с ОПЗ 2.0</h3>
                                <p>
                                    Предлагайте следующий визит после посещения и отслеживайте, сколько
                                    клиентов записались снова.
                                </p>
                                <router-link class="btn r20-lime" :to="opzMarketplaceLink">
                                    Подробнее об ОПЗ 2.0 <span aria-hidden="true">↗</span>
                                </router-link>
                            </div>
                        </div>
                    </section>
                </div>
                <div v-else-if="loading" class="r20-placeholder" aria-busy="true">Загружаем отчёт…</div>
            </div>
        </main>

        <!-- Та же модалка, что в «Сервисных триггерах». Монтируем только по клику:
             при монтировании она сама открывается, если услуги ещё ни разу не распределяли -->
        <AdditionalServicesModal v-if="servicesDialogMounted" v-model="servicesDialog" />
        <TrialOfferModal
            v-if="trialOpen && businessPulseTrial"
            :offer="businessPulseTrial"
            :activating="activatingTrial"
            @close="closeTrial"
            @activate="activateTrial"
        />
    </div>
</template>

<script>
import Breadcrumbs from '@/components/Breadcrumbs.vue'
import AdditionalServicesModal from '@/components/chisai/GCB2/bot/AdditionalServicesModal.vue'
import MetricDelta from './businessPulseReport/MetricDelta.vue'
import { CONNECTOR_TYPES } from '@/vars/general'
import { checkRole } from '@/helpers/roles'
import { getBusinessPulseReportV2 } from '@/api/chisai/GCB2'
import config from '@root/config'
import TrialOfferModal from '@/modules/marketplace/components/TrialOfferModal.vue'
import { findTrialOffer, trialPeriodLabel } from '@/modules/marketplace/trialOffers'
import { activateIntegrationTrial } from '@/api/marketplace'
import { useMarketplaceStore } from '@/store/stores/marketplace'
import {
    adjacentRange,
    compareRange,
    days,
    fmtDate,
    fromTo,
    interval,
    iso,
    parseDate,
    presetRange,
    projectToday,
    addDays,
    monthStart,
} from './businessPulseReport/dates'

const CURRENCY_KEYS = new Set([
    'revenue',
    'daily',
    'average',
    'services',
    'products',
    'additional',
    'additionalAverage',
])
const RATE_KEYS = new Set([
    'returnRate',
    'missingRate',
    'repeatShare',
    'newShare',
    'secondRate',
    'secondMissingRate',
    'permanentShare',
    'opzRate',
])
// Для этих показателей рост — плохо
const INVERSE_KEYS = new Set([
    'missing',
    'secondMissing',
    'secondMissingRate',
    'missingRate',
    'canceled',
    'canceledRepeat',
    'canceledNew',
])
const CHART_STEPS = { hour: 'По времени', day: 'По дням', month: 'По месяцам', year: 'По годам' }
const MONTH_NAMES = [
    'январь',
    'февраль',
    'март',
    'апрель',
    'май',
    'июнь',
    'июль',
    'август',
    'сентябрь',
    'октябрь',
    'ноябрь',
    'декабрь',
]
const MONTH_SHORT = [
    'янв.',
    'февр.',
    'март',
    'апр.',
    'май',
    'июнь',
    'июль',
    'авг.',
    'сент.',
    'окт.',
    'нояб.',
    'дек.',
]

function plural(n, one, few, many) {
    const mod10 = n % 10
    const mod100 = n % 100
    if (mod10 === 1 && mod100 !== 11) return one
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
    return many
}

const numberFormat = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 })
const numberFormat1 = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 1 })
const numberFormat2 = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 })

function flatten(snapshot) {
    if (!snapshot) return null
    return {
        ...snapshot.finance,
        ...snapshot.clients,
        ...snapshot.retention,
        ...snapshot.bookings,
        opz: snapshot.opz.opz,
        opzVisits: snapshot.opz.visits,
        opzRate: snapshot.opz.rate,
        series: snapshot.series,
    }
}

export default {
    name: 'BusinessPulseReport',
    components: { Breadcrumbs, AdditionalServicesModal, MetricDelta, TrialOfferModal },
    data() {
        const today = projectToday(this.projectTimezone())
        const week = presetRange('week', today)
        return {
            servicesDialog: false,
            trialOpen: false,
            activatingTrial: false,
            servicesDialogMounted: false,
            report: null,
            loading: false,
            error: false,
            requestId: 0,
            // По умолчанию: все сотрудники, текущая неделя, сравнение с предыдущим периодом
            preset: 'week',
            start: week.start,
            end: week.end,
            comparison: 'previous',
            // null — все сотрудники
            selectedStaff: null,
            employeeOpen: false,
            chart: 'revenue',
            tip: null,
            chartWidth: 900,
            chartHeight: 223,
            allocationFlash: false,
            picker: {
                open: false,
                start: '',
                end: '',
                pickingEnd: false,
                mode: 'days',
                view: monthStart(parseDate(week.start)),
                error: '',
            },
            weekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
            monthNames: MONTH_NAMES,
            presets: [
                { value: 'day', label: 'День', title: 'Сегодня' },
                { value: 'week', label: 'Неделя', title: 'Текущая неделя: с понедельника по воскресенье' },
                { value: 'month', label: 'Месяц', title: 'Текущий календарный месяц целиком' },
                { value: 'quarter', label: '3 месяца', title: 'Последние 3 полных календарных месяца' },
                { value: 'half', label: '6 месяцев', title: 'Последние 6 полных календарных месяцев' },
                { value: 'year', label: 'Год', title: 'Последние 12 полных календарных месяцев' },
                { value: 'all', label: 'Все время', title: 'Вся доступная история до сегодняшнего дня' },
            ],
            chartTabs: [
                { value: 'revenue', label: 'Выручка' },
                { value: 'bookings', label: 'Записи' },
                { value: 'average', label: 'Средний чек' },
            ],
            retentionCards: [
                {
                    key: 'returned',
                    rate: 'returnRate',
                    base: 'baselineClients',
                    title: 'Возвращаемость — общая',
                    share: 'Доля вернувшихся',
                    tone: 'mint',
                    before: 'Из клиентов с завершённым визитом',
                    middle: ' считаем тех, кто пришёл снова',
                    after: '',
                },
                {
                    key: 'missing',
                    rate: 'missingRate',
                    base: 'baselineClients',
                    title: 'Не вернулись — общая',
                    share: 'Доля не вернувшихся',
                    tone: 'sand',
                    before: 'Клиенты с завершённым визитом',
                    middle: ', которые не пришли',
                    after: ' Они могут вернуться позднее.',
                },
                {
                    key: 'secondClients',
                    rate: 'secondRate',
                    base: 'eligible',
                    title: 'Вернулись на второй визит',
                    share: 'Доля от клиентов с первым визитом',
                    tone: '',
                    before: 'Из клиентов с первым завершённым визитом',
                    middle: ' считаем тех, кто пришёл на второй визит',
                    after: '',
                },
                {
                    key: 'secondMissing',
                    rate: 'secondMissingRate',
                    base: 'eligible',
                    title: 'Не вернулись на второй визит',
                    share: 'Доля от клиентов с первым визитом',
                    tone: 'peach',
                    before: 'Клиенты с первым завершённым визитом',
                    middle: ', которые не пришли на второй визит',
                    after: ' Они могут вернуться позднее.',
                },
            ],
        }
    },
    computed: {
        project() {
            return this.$store.getters.projectById(this.$route.params.id)
        },
        hasBusinessPulseAccess() {
            const connector = (this.project?.connectors || []).find(
                item => item.connectorType === CONNECTOR_TYPES.GCB2
            )
            return connector?.businessPulseReport === true
        },
        businessPulseTrial() {
            return findTrialOffer('businessPulseReport')
        },
        trialButtonText() {
            const days = this.businessPulseTrial?.days || 1
            return `Попробовать ${trialPeriodLabel(days)}`
        },
        businessPulseIntegration() {
            const moduleId = config.marketplaceModulesIds[config.buildType]?.businessPulseReport
            if (!moduleId || !this.$route.params.id) return null
            return (
                useMarketplaceStore()
                    .allIntgrations(this.$route.params.id)
                    .find(item => String(item.id) === String(moduleId)) ?? null
            )
        },
        showTrialButton() {
            return Boolean(this.businessPulseIntegration) && this.businessPulseIntegration.isTrialUsed !== true
        },
        breadcrumbs() {
            return [
                { text: 'Ваши проекты', to: '/projects' },
                {
                    text: (this.project && this.project.name) || 'Проект',
                    to: `/project/${this.$route.params.id}`,
                },
                { text: 'Отчёт 2.0' },
            ]
        },
        today() {
            return projectToday(this.projectTimezone())
        },
        canConfigureServices() {
            if (!this.project) return false
            const hasYClients = (this.project.connectors || []).some(
                el => el.connectorType === CONNECTOR_TYPES.yClients
            )
            return hasYClients && checkRole(this.project.id, 'project:rolesSettings:save')
        },
        opzMarketplaceLink() {
            return `/project/${this.$route.params.id}/marketplace/integration/${
                config.marketplaceModulesIds[config.buildType].opz2
            }`
        },
        // Период «Всё время» начинается с первой записи — её дату знает только бэк
        currentRange() {
            if (this.preset === 'all') {
                const periods = this.report && this.report.periods
                return periods && this.report.request.period === 'all'
                    ? { start: periods.current.start, end: periods.current.end }
                    : null
            }
            return { start: this.start, end: this.end }
        },
        // Для «Всего времени» сравнивать не с чем: до первой записи данных нет
        comparisonRange() {
            if (!this.currentRange || this.preset === 'all') return null
            return compareRange(this.currentRange, this.comparison)
        },
        baselineRange() {
            return this.currentRange ? adjacentRange(this.currentRange) : null
        },
        t() {
            return this.currentRange ? fromTo(this.currentRange) : 'за всё время'
        },
        baselineT() {
            return this.baselineRange ? fromTo(this.baselineRange) : ''
        },
        startLabel() {
            return this.currentRange ? fmtDate(parseDate(this.currentRange.start)) : ''
        },
        periodTag() {
            return this.currentRange ? interval(this.currentRange) : 'Всё время'
        },
        comparisonHelp() {
            if (this.comparison === 'none') return 'Показываем только фактические значения.'
            if (this.preset === 'all') return 'Для всего времени сравнение не строится.'
            if (this.comparison === 'year') return 'Те же даты годом раньше.'
            if (!this.currentRange) return ''
            const range = { start: parseDate(this.currentRange.start), end: parseDate(this.currentRange.end) }
            const months = this.fullMonths(this.currentRange)
            if (months === 1) return 'Предыдущий календарный месяц.'
            if (months)
                return `${months} ${plural(
                    months,
                    'полный календарный месяц',
                    'полных календарных месяца',
                    'полных календарных месяцев'
                )}.`
            const count = days(range)
            return `${count} ${plural(count, 'день', 'дня', 'дней')} подряд перед ${fmtDate(range.start)}.`
        },
        staffOptions() {
            return (this.report && this.report.staff) || []
        },
        allStaffSelected() {
            return this.selectedStaff === null
        },
        someStaffSelected() {
            return this.selectedStaff !== null && this.selectedStaff.length > 0
        },
        noStaffSelected() {
            return this.selectedStaff !== null && this.selectedStaff.length === 0
        },
        employeeSummary() {
            if (this.selectedStaff === null) return 'Все сотрудники'
            if (!this.selectedStaff.length) return 'Не выбраны'
            if (this.selectedStaff.length === 1) {
                const item = this.staffOptions.find(option => option.id === this.selectedStaff[0])
                return item ? item.name : 'Выбрано: 1'
            }
            return `Выбрано: ${this.selectedStaff.length}`
        },
        additionalUnconfigured() {
            const warnings = (this.report && this.report.meta && this.report.meta.warnings) || []
            return warnings.includes('additionalServicesUnconfigured')
        },
        x() {
            return this.report ? flatten(this.report.current) : null
        },
        b() {
            return this.report ? flatten(this.report.comparison) : null
        },
        step() {
            return (this.report && this.report.step) || 'day'
        },
        chartStepLabel() {
            return CHART_STEPS[this.step]
        },
        chartKey() {
            return this.chart === 'bookings' ? 'bookings' : this.chart === 'average' ? 'average' : 'revenue'
        },
        chartTotal() {
            return this.x ? this.x[this.chartKey] : null
        },
        chartCaption() {
            const caption = { revenue: 'Выручка', bookings: 'Всего записей', average: 'Средний чек' }[
                this.chart
            ]
            return `${caption} ${this.t}`
        },
        chartPoints() {
            return this.pointsOf(this.x)
        },
        chartComparisonPoints() {
            return this.b ? this.pointsOf(this.b) : []
        },
        chartExtrema() {
            const valid = this.chartPoints.filter(point => point.value !== null)
            return {
                min: valid.reduce((best, point) => (!best || point.value < best.value ? point : best), null),
                max: valid.reduce((best, point) => (!best || point.value > best.value ? point : best), null),
            }
        },
        chartGeometry() {
            const width = Math.max(280, this.chartWidth)
            const height = this.chartHeight
            const left = 44
            const bottom = 27
            const top = 8
            const right = 9
            const plotW = width - left - right
            const plotH = height - top - bottom
            const curr = this.chartPoints
            const prev = this.chartComparisonPoints
            const hasComparison = Boolean(this.b)
            const n = Math.max(curr.length, prev.length, 1)
            const values = [...curr, ...prev].map(point => point.value || 0)
            const max = Math.max(...values, 1) * 1.08
            const gw = plotW / n
            const bw = Math.min(21, gw * (hasComparison ? 0.27 : 0.55))
            const grid = [0, 1, 2, 3].map(i => {
                const value = max * (1 - i / 3)
                return {
                    y: top + (plotH * i) / 3,
                    label:
                        value >= 1000000
                            ? `${numberFormat1.format(value / 1000000)} млн`
                            : value >= 1000
                            ? `${numberFormat.format(value / 1000)} тыс`
                            : numberFormat.format(value),
                }
            })
            const bars = []
            const labels = []
            const jump = Math.ceil(n / (width < 500 ? 5 : 10))
            for (let i = 0; i < n; i++) {
                const center = left + gw * (i + 0.5)
                const p = prev[i]
                if (hasComparison && p && p.value !== null) {
                    const h = (p.value / max) * plotH
                    bars.push({
                        key: `p${i}`,
                        index: i,
                        series: 'previous',
                        x: center - bw - 1,
                        y: top + plotH - h,
                        width: bw,
                        height: h,
                        aria: `Сравнение. ${this.pointLabel(p)}: ${this.formatChartValue(p.value)}`,
                    })
                }
                const c = curr[i]
                if (c && c.value !== null) {
                    const h = (c.value / max) * plotH
                    bars.push({
                        key: `c${i}`,
                        index: i,
                        series: 'current',
                        x: hasComparison ? center + 1 : center - bw / 2,
                        y: top + plotH - h,
                        width: bw,
                        height: h,
                        aria: `${this.pointLabel(c)}: ${this.formatChartValue(c.value)}`,
                    })
                }
                const lastTooClose =
                    i === n - 1 && jump > 1 && (n - 1) % jump !== 0 && (n - 1) % jump < jump / 2
                if (c && (i % jump === 0 || i === n - 1) && !lastTooClose)
                    labels.push({ index: i, x: center, text: this.axisLabel(c) })
            }
            return { width, height, left, right, grid, bars, labels }
        },
        pickerMonthLabel() {
            return `${MONTH_NAMES[this.picker.view.getUTCMonth()]} ${this.picker.view.getUTCFullYear()}`
        },
        pickerYear() {
            return this.picker.view.getUTCFullYear()
        },
        pickerMonth() {
            return this.picker.view.getUTCMonth()
        },
        pickerYears() {
            const current = parseDate(this.today).getUTCFullYear()
            const years = []
            for (let year = current; year >= 2000; year--) years.push(year)
            return years
        },
        pickerCells() {
            const month = this.picker.view
            const offset = (month.getUTCDay() + 6) % 7
            const daysInMonth = new Date(
                Date.UTC(month.getUTCFullYear(), month.getUTCMonth() + 1, 0)
            ).getUTCDate()
            const count = Math.ceil((offset + daysInMonth) / 7) * 7
            const cells = []
            for (let i = 0; i < count; i++) {
                const dayNumber = i - offset + 1
                if (dayNumber < 1 || dayNumber > daysInMonth) {
                    cells.push({ key: `b${i}`, date: null })
                    continue
                }
                const id = iso(new Date(Date.UTC(month.getUTCFullYear(), month.getUTCMonth(), dayNumber)))
                const selected = Boolean(this.picker.end) && id >= this.picker.start && id <= this.picker.end
                cells.push({ key: id, date: id, day: dayNumber, selected })
            }
            return cells
        },
        pickerValidation() {
            const { start, end } = this.picker
            if (!this.validISO(start) || !this.validISO(end)) return 'Укажите первую и последнюю дату.'
            if (start > end) return 'Последняя дата должна быть не раньше первой.'
            if (days({ start: parseDate(start), end: parseDate(end) }) > 3660)
                return 'Выберите интервал не больше 10 лет.'
            return ''
        },
        pickerPreview() {
            if (this.pickerValidation) {
                return this.picker.pickingEnd && this.validISO(this.picker.start)
                    ? `С ${fmtDate(parseDate(this.picker.start))} · выберите последнюю дату`
                    : 'Проверьте даты'
            }
            return interval(this.picker)
        },
    },
    watch: {
        // после закрытия модалки пересчитываем отчёт: если услуги распределили,
        // показатели по доп. услугам появятся сразу
        servicesDialog(value, previous) {
            if (previous && !value) this.loadReport()
        },
        '$route.params.id'() {
            this.selectedStaff = null
            this.trialOpen = false
            this.setPreset('week')
            if (!this.hasBusinessPulseAccess) this.loadTrialStatus()
        },
        hasBusinessPulseAccess: {
            immediate: true,
            handler(value) {
                if (value) this.loadReport()
                else this.loadTrialStatus()
            },
        },
    },
    mounted() {
        document.addEventListener('click', this.onDocumentClick)
        if (window.ResizeObserver) {
            this.resizeObserver = new ResizeObserver(() => this.measureChart())
            this.$nextTick(() => this.observeChart())
        } else {
            window.addEventListener('resize', this.measureChart)
        }
    },
    updated() {
        this.observeChart()
    },
    beforeDestroy() {
        this.requestId += 1
        document.removeEventListener('click', this.onDocumentClick)
        window.removeEventListener('resize', this.measureChart)
        if (this.resizeObserver) this.resizeObserver.disconnect()
    },
    methods: {
        fmtDate,
        parseDate,
        interval,
        fromTo,
        projectTimezone() {
            const project =
                this.$store && this.$store.getters && this.$store.getters.projectById
                    ? this.$store.getters.projectById(this.$route.params.id)
                    : null
            return (project && project.timezone) || 'Europe/Moscow'
        },
        fullMonths(range) {
            const start = parseDate(range.start)
            const end = parseDate(range.end)
            const endOfMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth() + 1, 0))
            if (start.getUTCDate() !== 1 || iso(end) !== iso(endOfMonth)) return 0
            return (
                (end.getUTCFullYear() - start.getUTCFullYear()) * 12 +
                end.getUTCMonth() -
                start.getUTCMonth() +
                1
            )
        },
        closeTrial() {
            if (this.activatingTrial) return
            this.trialOpen = false
        },
        async loadTrialStatus() {
            const projectId = this.$route.params.id
            if (!projectId) return
            await useMarketplaceStore().fetchIntegrations(projectId)
        },
        async activateTrial() {
            const integrationId = this.businessPulseIntegration && this.businessPulseIntegration.id
            const projectId = this.$route.params.id
            if (this.activatingTrial || !projectId || !integrationId) return
            this.activatingTrial = true
            const { error } = await activateIntegrationTrial({
                projectId: String(projectId),
                integrationId: String(integrationId),
            })
            this.activatingTrial = false
            if (error) {
                this.$store.dispatch('callNotify', 'Не удалось активировать пробный период')
                return
            }
            this.trialOpen = false
            await useMarketplaceStore().fetchIntegrations(String(projectId))
            await this.$store.dispatch('project/fetchProject', projectId)
        },
        buyModule() {
            const moduleId = config.marketplaceModulesIds[config.buildType]?.businessPulseReport
            if (!moduleId || !this.$route.params.id) return
            this.$router
                .push(`/project/${this.$route.params.id}/marketplace/integration/${moduleId}`)
                .catch(() => {})
        },
        async loadReport() {
            if (!this.hasBusinessPulseAccess) return
            const requestId = ++this.requestId
            this.tip = null
            if (this.noStaffSelected) {
                this.loading = false
                return
            }
            this.loading = true
            this.error = false
            const request = {
                projectId: this.$route.params.id,
                start: this.preset === 'all' ? null : this.start,
                end: this.preset === 'all' ? null : this.end,
                period: this.preset === 'all' ? 'all' : 'custom',
                comparison: this.comparison,
                staffIds: this.selectedStaff,
            }
            const { data, error } = await getBusinessPulseReportV2(request)
            if (requestId !== this.requestId) return
            this.loading = false
            this.error = Boolean(error)
            if (!error) this.report = { ...data, request }
        },
        setPreset(value) {
            this.preset = value
            if (value !== 'all') {
                const range = presetRange(value, this.today)
                this.start = range.start
                this.end = range.end
            }
            this.closePicker()
            this.loadReport()
        },
        setComparison(value) {
            this.comparison = value
            this.loadReport()
        },
        isStaffSelected(id) {
            return this.selectedStaff === null || this.selectedStaff.includes(id)
        },
        toggleAllStaff(checked) {
            this.selectedStaff = checked ? null : []
            this.loadReport()
        },
        toggleStaff(id, checked) {
            const all = this.staffOptions.map(item => item.id)
            const current = this.selectedStaff === null ? all : this.selectedStaff
            const next = checked ? [...new Set([...current, id])] : current.filter(item => item !== id)
            this.selectedStaff = all.every(item => next.includes(item)) ? null : next
            this.loadReport()
        },
        onDocumentClick(event) {
            const path = event.composedPath ? event.composedPath() : []
            if (this.employeeOpen && this.$refs.employeePicker && !path.includes(this.$refs.employeePicker))
                this.employeeOpen = false
            if (this.picker.open && this.$refs.datePicker && !path.includes(this.$refs.datePicker))
                this.closePicker()
        },
        // Календарь
        openPicker() {
            const range = this.currentRange || { start: this.start, end: this.end }
            this.picker = {
                open: true,
                start: range.start,
                end: range.end,
                pickingEnd: false,
                mode: 'days',
                view: monthStart(parseDate(range.start)),
                error: '',
            }
        },
        closePicker(restoreFocus = false) {
            const wasOpen = this.picker.open
            this.picker.open = false
            if (wasOpen && restoreFocus && this.$refs.calendarTrigger) this.$refs.calendarTrigger.focus()
        },
        navigatePicker(amount) {
            this.picker.view = monthStart(
                this.picker.view,
                this.picker.mode === 'months' ? amount * 12 : amount
            )
        },
        setPickerYear(year) {
            this.picker.view = new Date(Date.UTC(Number(year), this.picker.view.getUTCMonth(), 1))
        },
        setPickerMonth(month) {
            this.picker.view = new Date(Date.UTC(this.picker.view.getUTCFullYear(), month, 1))
            this.picker.mode = 'days'
        },
        pickDate(value) {
            if (!this.picker.pickingEnd) {
                this.picker.start = value
                this.picker.end = ''
                this.picker.pickingEnd = true
            } else {
                const start = this.picker.start
                this.picker.start = value < start ? value : start
                this.picker.end = value < start ? start : value
                this.picker.pickingEnd = false
            }
            this.picker.error = ''
        },
        validISO(value) {
            if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return false
            const date = parseDate(value)
            return Number.isFinite(+date) && iso(date) === value
        },
        pickerInputDate(value) {
            if (!this.validISO(value)) return value || ''
            const date = parseDate(value)
            return `${String(date.getUTCDate()).padStart(2, '0')}.${String(date.getUTCMonth() + 1).padStart(
                2,
                '0'
            )}.${date.getUTCFullYear()}`
        },
        parsePickerInput(value) {
            const raw = value.trim()
            if (this.validISO(raw)) return raw
            const digits = raw.replace(/[./\s]/g, '')
            if (!/^\d{6}(?:\d{2})?$/.test(digits)) return raw
            const year = digits.slice(4)
            const result = `${year.length === 2 ? `20${year}` : year}-${digits.slice(2, 4)}-${digits.slice(
                0,
                2
            )}`
            return this.validISO(result) ? result : raw
        },
        onPickerInput(key, value) {
            this.picker[key] = this.parsePickerInput(value)
            this.picker.pickingEnd = false
            this.picker.mode = 'days'
            if (this.validISO(this.picker.start)) this.picker.view = monthStart(parseDate(this.picker.start))
            this.picker.error = this.pickerValidation
        },
        applyPicker() {
            if (this.pickerValidation) {
                this.picker.error = this.pickerValidation
                return
            }
            this.start = this.picker.start
            this.end = this.picker.end
            // Если даты совпали с быстрым периодом — подсвечиваем его
            const matched = this.presets.find(item => {
                if (item.value === 'all') return false
                const range = presetRange(item.value, this.today)
                return range.start === this.start && range.end === this.end
            })
            this.preset = matched ? matched.value : 'custom'
            this.closePicker(true)
            this.loadReport()
        },
        // Значения
        num(value, digits = 0) {
            if (value === null || value === undefined || !Number.isFinite(Number(value))) return '—'
            return (digits === 2 ? numberFormat2 : digits === 1 ? numberFormat1 : numberFormat).format(value)
        },
        formatValue(key, value) {
            if (value === null || value === undefined) return '—'
            if (CURRENCY_KEYS.has(key)) return `${this.num(value, 2)} ₽`
            if (RATE_KEYS.has(key)) return `${this.num(value, 1)}%`
            return this.num(value)
        },
        formatChartValue(value) {
            return this.chart === 'bookings' ? this.num(value) : this.formatValue('revenue', value)
        },
        // Отклонение от сравниваемого периода: в процентах и в абсолютных значениях
        delta(key, unit = '') {
            if (!this.b)
                return {
                    tone: 'neutral',
                    main: 'Сравнение отключено',
                    plain: true,
                    extra: null,
                    extraTitle: null,
                }
            const current = this.x[key]
            const prior = this.b[key]
            if (current === null || current === undefined || prior === null || prior === undefined) {
                return { tone: 'neutral', main: '—', extra: null, extraTitle: null }
            }
            const difference = current - prior
            const change = prior ? (difference / prior) * 100 : null
            const good = INVERSE_KEYS.has(key) ? difference < 0 : difference > 0
            const flat = difference === 0 || (change !== null && Math.abs(change) < 0.05)
            const main =
                change === null
                    ? '—'
                    : flat
                    ? '0%'
                    : `${change > 0 ? '+' : '−'}${this.num(Math.abs(change), 1)}%`
            let extra = null
            let extraTitle = null
            if (!RATE_KEYS.has(key)) {
                const absolute = CURRENCY_KEYS.has(key)
                    ? this.formatValue('revenue', Math.abs(difference))
                    : this.num(Math.abs(difference))
                extra = `(${difference < 0 ? '−' : difference > 0 ? '+' : ''}${absolute}${
                    unit ? ` ${unit}` : ''
                })`
            }
            if (key === 'opzRate') {
                const diff = this.x.opz - this.b.opz
                const n = Math.abs(diff)
                const tail =
                    n % 10 === 1 && n % 100 !== 11
                        ? 'визит'
                        : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)
                        ? 'визита'
                        : 'визитов'
                extra = `(${diff < 0 ? '−' : diff > 0 ? '+' : ''}${this.num(n)} ${tail})`
                extraTitle = 'Изменение количества завершённых визитов с ОПЗ'
            }
            return { tone: flat ? 'neutral' : good ? 'positive' : 'negative', main, extra, extraTitle }
        },
        // График
        pointsOf(snapshot) {
            if (!snapshot) return []
            return snapshot.series.map(point => ({
                ...point,
                value:
                    this.chart === 'average'
                        ? point.completed
                            ? point.revenue / point.completed
                            : null
                        : this.chart === 'revenue'
                        ? point.revenue
                        : point.bookings,
            }))
        },
        hourLabel(point) {
            const hour = String(point.key).padStart(2, '0')
            return `${hour}:00–${hour}:59`
        },
        pointLabel(point) {
            if (this.step === 'hour') return `${fmtDate(parseDate(point.start))} · ${this.hourLabel(point)}`
            return interval(point)
        },
        chartExtremaLabel(point) {
            return this.step === 'day' ? fmtDate(parseDate(point.start)) : this.pointLabel(point)
        },
        axisLabel(point) {
            if (this.step === 'hour') return `${String(point.key).padStart(2, '0')}:00`
            const date = parseDate(point.key)
            if (this.step === 'year') return String(date.getUTCFullYear())
            if (this.step === 'month') return MONTH_SHORT[date.getUTCMonth()]
            return `${String(date.getUTCDate()).padStart(2, '0')}.${String(date.getUTCMonth() + 1).padStart(
                2,
                '0'
            )}`
        },
        showTip(index, event) {
            const wrap = this.$refs.chartWrap
            if (!wrap) return
            const lines = []
            for (const [label, point] of [
                ['Показываем', this.chartPoints[index]],
                ['Сравниваем', this.chartComparisonPoints[index]],
            ]) {
                if (!point) continue
                const value =
                    point.value === null
                        ? '—'
                        : this.chart === 'bookings'
                        ? `${this.num(point.value)} записей`
                        : this.formatValue('revenue', point.value)
                lines.push(`${label}: ${this.pointLabel(point)} · ${value}`)
            }
            const rect = event.target.getBoundingClientRect()
            const wrapRect = wrap.getBoundingClientRect()
            this.tip = {
                lines,
                left: Math.max(0, Math.min(rect.left - wrapRect.left - 40, wrapRect.width - 260)),
                top: Math.max(0, rect.top - wrapRect.top - 22 * lines.length - 24),
            }
        },
        onChartTabsKeydown(event) {
            if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
            event.preventDefault()
            const tabs = this.chartTabs.map(tab => tab.value)
            const index = tabs.indexOf(this.chart)
            const next =
                event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                    ? tabs.length - 1
                    : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length
            this.chart = tabs[next]
            this.$nextTick(() => {
                const ref = this.$refs[`chart-tab-${tabs[next]}`]
                if (ref && ref[0]) ref[0].focus()
            })
        },
        observeChart() {
            const wrap = this.$refs.chartWrap
            if (!this.resizeObserver || !wrap || wrap === this.observedChart) return
            this.resizeObserver.disconnect()
            this.resizeObserver.observe(wrap)
            this.observedChart = wrap
            this.measureChart()
        },
        measureChart() {
            const wrap = this.$refs.chartWrap
            if (!wrap) return
            this.chartWidth = wrap.clientWidth || this.chartWidth
            this.chartHeight = wrap.clientHeight || this.chartHeight
        },
        // Доп. услуги
        openServicesDialog() {
            this.servicesDialogMounted = true
            this.servicesDialog = true
        },
        onAllocationLink() {
            if (!this.additionalUnconfigured || !this.$refs.allocationBar) {
                this.openServicesDialog()
                return
            }
            this.$refs.allocationBar.scrollIntoView({ behavior: 'smooth', block: 'center' })
            if (this.$refs.distributeButton) this.$refs.distributeButton.focus({ preventScroll: true })
            this.allocationFlash = true
            setTimeout(() => (this.allocationFlash = false), 1500)
        },
        scrollTo(id) {
            const target = document.getElementById(id)
            if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
        },
    },
}
</script>

<style lang="scss">
@import './businessPulseReport/report20.scss';

// Свои дополнения к макету: состояние загрузки и ошибки, которых в макете нет
html .business-pulse-v2 {
    background: #f3f4fb;
    // App.vue даёт .content-wrapper нижний отступ 136px под футер — здесь он
    // оставлял пустую полосу между отчётом и футером
    &.content-wrapper {
        margin-bottom: 0;
    }
}
html .report20 {
    .r20-body--loading {
        opacity: 0.55;
        transition: opacity 0.15s;
        pointer-events: none;
    }
    .r20-load-error {
        margin-top: 16px;
        font-size: 13px;
    }
    .r20-placeholder {
        margin-top: 27px;
        padding: 40px;
        text-align: center;
        color: var(--muted);
        background: white;
        border: 1px solid var(--line);
        border-radius: 16px;
    }
    .opz-promo .btn {
        text-decoration: none;
    }
}
</style>
